/**
 * Regra única de disponibilidade de chip físico no link de venda direta.
 *
 * Duas condições independentes precisam valer:
 *  - a marca declara em /busca-uuid que vende chip físico
 *    (`venda_chip_fisico`, vindo de FRANQUIA_MARCA_PROPRIA);
 *  - há chip físico em estoque.
 *
 * A configuração sozinha não basta: uma marca habilitada sem estoque não tem
 * o que entregar, e o fluxo precisa continuar caindo no atendimento por
 * WhatsApp nesse caso.
 *
 * Fica aqui, e não nos componentes, porque a decisão é da marca e o
 * link de venda direta não pode ter lógica de MVNO dentro de componente
 * React (ver CLAUDE.md).
 */
export interface PhysicalChipAvailabilityInput {
  physicalStock: boolean
  physicalChipEnabled: boolean
}

export function isPhysicalChipAvailable({
  physicalStock,
  physicalChipEnabled,
}: PhysicalChipAvailabilityInput): boolean {
  return physicalChipEnabled && physicalStock
}

/**
 * Normaliza o campo vindo da API.
 *
 * A ausência do campo é tratada como habilitado de propósito: se a API ainda
 * não tiver a versão que expõe `venda_chip_fisico`, `undefined` desabilitaria
 * o chip físico de todas as marcas de uma vez.
 */
export function normalizePhysicalChipEnabled(value: boolean | undefined): boolean {
  return value ?? true
}

/**
 * Visibilidade do botão de chip avulso (venda de chip físico sem plano).
 *
 * Duas regras se somam:
 *  - a marca precisa vender chip físico. Diferente das demais condições, essa
 *    veta o botão de forma incondicional: chip avulso *é* a venda de chip
 *    físico que a marca declarou não fazer, então nem o caso "sem estoque de
 *    nada" pode trazê-lo de volta;
 *  - preservada do comportamento original: o botão aparece quando o chip
 *    físico está disponível, e também quando não há estoque de nada — nesse
 *    caso a navegação segue até o fim e o checkout cai no atendimento por
 *    WhatsApp.
 */
export interface ChipOnlySaleVisibilityInput {
  chipOnlySale: boolean
  physicalChipEnabled: boolean
  physicalChipAvailable: boolean
  virtualStock: boolean
}

export function isChipOnlySaleVisible({
  chipOnlySale,
  physicalChipEnabled,
  physicalChipAvailable,
  virtualStock,
}: ChipOnlySaleVisibilityInput): boolean {
  if (!chipOnlySale || !physicalChipEnabled) return false

  return physicalChipAvailable || !virtualStock
}
